import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, ConfirmDialog, FormActionBar, WindowContent, toast } from '@/shared/ui';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import {
  getGetWwwWebFilterPlacementOrderQueryKey,
  getGetWwwWebFilterQueryKey,
  useDeleteWwwWebFilter,
  useGetWwwWebFilter,
  useListWwwWebProjects,
} from '@/api/generated/www-web-filters/www-web-filters';
import { WEB_FILTER_LIST_KEY } from '../internal/webFiltersQueryKeys';
import { webFilterFormAgenda } from './webFilterFormAgenda';
import { useWebFilterForm } from './useWebFilterForm';
import {
  emptyWebFilterFormValues,
  fromGetWebFilterResponse,
  type WebFilterFormValues,
} from './webFilterFormSchema';
import { FilterIdentityPanel } from './components/FilterIdentityPanel';
import { ProductFilterPanel } from './components/ProductFilterPanel';
import { WebDisplayPanel } from './components/WebDisplayPanel';
import { PlacementOrderPanel } from './components/PlacementOrderPanel';
import { selectEnabledKindsFromForm, usePlacementOrder } from '../internal/usePlacementOrder';

interface WebFilterFormPageProps {
  layerId: string;
}

export function WebFilterFormPage({ layerId }: WebFilterFormPageProps) {
  const { t } = useTranslation('www');
  const { open, state } = useAgendaParams(webFilterFormAgenda, layerId);
  const webFilterId = open.webFilterId;
  const isEdit = Boolean(webFilterId);
  const createProjectId = state.webProjectId;

  const detailQuery = useGetWwwWebFilter(webFilterId ?? '', {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  const projectsQuery = useListWwwWebProjects(
    { includeWebProjectId: isEdit ? detailQuery.data?.webProjectId : undefined },
    { query: { enabled: !isEdit || Boolean(detailQuery.data) } }
  );

  if (isEdit && detailQuery.isLoading) {
    return (
      <p className="text-phx-text-muted text-phx-sm p-phx-5" data-testid="web-filter-form">
        {t('webFilter.loading')}
      </p>
    );
  }

  if (isEdit && detailQuery.isError) {
    return (
      <div className="p-phx-5" data-testid="web-filter-form">
        <Alert severity="error" title={t('webFilter.loadErrorTitle')}>
          {t('webFilter.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  if (!isEdit && (createProjectId == null || createProjectId <= 0)) {
    return (
      <div className="p-phx-5" data-testid="web-filter-form">
        <Alert severity="warning" title={t('webFilter.missingProjectTitle')}>
          {t('webFilter.missingProjectDescription')}
        </Alert>
      </div>
    );
  }

  const projectName =
    detailQuery.data?.webProjectName ??
    projectsQuery.data?.find((p) => p.id === createProjectId)?.name ??
    '';

  const initialValues: WebFilterFormValues = detailQuery.data
    ? fromGetWebFilterResponse(detailQuery.data)
    : emptyWebFilterFormValues(createProjectId!, projectName);

  const projectOptions =
    projectsQuery.data?.map((p) => ({
      value: String(p.id),
      label: p.isActive ? p.name : `${p.name} (${t('webFilter.inactiveProject')})`,
    })) ?? [];

  return (
    <WebFilterFormView
      layerId={layerId}
      webFilterId={webFilterId}
      initialValues={initialValues}
      projectOptions={projectOptions}
      projectImmutable={isEdit}
    />
  );
}

interface WebFilterFormViewProps {
  layerId: string;
  webFilterId?: string;
  initialValues: WebFilterFormValues;
  projectOptions: { value: string; label: string }[];
  projectImmutable: boolean;
}

function WebFilterFormView({
  layerId,
  webFilterId,
  initialValues,
  projectOptions,
  projectImmutable,
}: WebFilterFormViewProps) {
  const { t } = useTranslation('www');
  const navigator = useNavigator();
  const queryClient = useQueryClient();

  const close = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const [liveValues, setLiveValues] = useState(initialValues);

  const placementOrderEditor = usePlacementOrder({
    webProjectId: liveValues.webProjectId,
    webFilterId,
    filterName: liveValues.name,
    enabledKinds: selectEnabledKindsFromForm(liveValues),
  });

  const { form, submit } = useWebFilterForm({
    webFilterId,
    initialValues,
    getPlacementOrder: () => placementOrderEditor.state,
    onSaved: (response, mode) => {
      placementOrderEditor.applySaveResponse(response.placementOrder);
      // Pořadí je per-projekt — invaliduj všechny varianty (create i edit klíče).
      void queryClient.invalidateQueries({
        queryKey: getGetWwwWebFilterPlacementOrderQueryKey(),
      });
      void queryClient.invalidateQueries({ queryKey: getGetWwwWebFilterQueryKey(response.id) });
      void queryClient.invalidateQueries({ queryKey: [WEB_FILTER_LIST_KEY] });
      toast({ severity: 'success', description: t('webFilter.toast.saved') });
      if (mode === 'created') {
        void navigator.replaceModal(webFilterFormAgenda, { webFilterId: response.id });
      }
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('webFilter.toast.errorTitle'),
        description: t('webFilter.toast.saveError'),
      });
    },
  });

  const deleteMutation = useDeleteWwwWebFilter();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!webFilterId) return;
    setDeleteConfirmOpen(false);
    try {
      await deleteMutation.mutateAsync({ id: webFilterId });
      void queryClient.invalidateQueries({
        queryKey: getGetWwwWebFilterPlacementOrderQueryKey(),
      });
      void queryClient.invalidateQueries({ queryKey: [WEB_FILTER_LIST_KEY] });
      toast({ severity: 'success', description: t('webFilter.toast.deleted') });
      close();
    } catch {
      toast({
        severity: 'error',
        title: t('webFilter.toast.errorTitle'),
        description: t('webFilter.toast.deleteError'),
      });
    }
  }, [close, deleteMutation, queryClient, t, webFilterId]);

  return (
    <>
      <WindowContent
        as="form"
        width="reading"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        footer={
          <form.Subscribe
            selector={(state) => ({
              canSubmit: state.canSubmit,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ canSubmit, isSubmitting }) => (
              <FormActionBar
                onBack={close}
                onSave={submit}
                saving={isSubmitting || deleteMutation.isPending}
                disabled={!canSubmit || deleteMutation.isPending}
                extraActions={
                  webFilterId ? (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={deleteMutation.isPending}
                      onClick={() => setDeleteConfirmOpen(true)}
                    >
                      {t('webFilter.actions.delete')}
                    </Button>
                  ) : null
                }
              />
            )}
          </form.Subscribe>
        }
      >
        <div className="gap-phx-3 flex flex-col" data-testid="web-filter-form">
          <FilterIdentityPanel
            form={form}
            webProjectOptions={projectOptions}
            projectImmutable={projectImmutable}
          />
          <ProductFilterPanel form={form} />
          <WebDisplayPanel form={form} />
          <form.Subscribe selector={(state) => state.values}>
            {(values) => <FormValuesSync values={values} onChange={setLiveValues} />}
          </form.Subscribe>
          <PlacementOrderPanel form={form} editor={placementOrderEditor} />
        </div>
      </WindowContent>

      {deleteConfirmOpen ? (
        <ConfirmDialog
          title={t('webFilter.deleteTitle')}
          message={t('webFilter.deleteConfirm')}
          confirmLabel={t('webFilter.actions.delete')}
          cancelLabel={t('webFilter.actions.cancel')}
          confirmVariant="danger"
          onConfirm={() => void handleDelete()}
          onCancel={() => setDeleteConfirmOpen(false)}
        />
      ) : null}
    </>
  );
}

function FormValuesSync({
  values,
  onChange,
}: {
  values: WebFilterFormValues;
  onChange: (values: WebFilterFormValues) => void;
}) {
  useEffect(() => {
    onChange(values);
  }, [onChange, values]);
  return null;
}
